import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  MentionTextarea,
  MultipleSelector,
  Skeleton,
  Switch,
  useDraft,
} from '@org/ui';
import type { MentionOption, Option } from '@org/ui';
import { toast } from 'sonner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeacherId } from '../../../../components/teacher-picker';
import { Search, X } from 'lucide-react';

export const Route = createFileRoute('/event/$eventId/scorecard/$scorecardId')({
  component: ScorecardPage,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

type AdditionalStudent = {
  studentId: string;
  fullName: string;
  email: string | null;
};

function ScorecardPage() {
  const { eventId, scorecardId } = Route.useParams();
  const navigate = useNavigate({ from: '/event/$eventId/scorecard/$scorecardId' });
  const utils = trpc.useUtils();
  const { t } = useTranslation('admin');
  const teacherId = useTeacherId();

  const { data, isLoading } = trpc.teacher.events.scorecardDetails.useQuery({ teacherId, eventId });
  const { mutateAsync: saveScorecard, isPending } = trpc.teacher.events.saveScorecard.useMutation({
    onSuccess: (result) => {
      utils.teacher.events.past.invalidate();
      utils.teacher.events.scorecardDetails.invalidate({ eventId });
      toast.success(t('common.toasts.saveSuccess', { entity: 'Class report' }));
      if (scorecardId === 'new' && result.scorecardId) {
        navigate({
          to: '/event/$eventId/scorecard/$scorecardId',
          params: { eventId, scorecardId: result.scorecardId },
          replace: true,
        });
      }
    },
    onError: () => {
      toast.error(t('common.toasts.retryError', { action: 'save class report' }));
    },
  });

  const isOpenEvent = data?.event.eventKind === 'open-event';
  const canAddStudents = isOpenEvent || data?.event.eventKind === 'closed-event';

  const {
    value: notes,
    setValue: setNotes,
    clear: clearNotesDraft,
  } = useDraft<string>(
    ['scorecard', eventId, scorecardId],
    data?.notes ?? '',
    { prefix: 'teacher-scorecard' },
  );

  const [presenceMap, setPresenceMap] = useState<Record<string, boolean>>({});
  const [additionalStudents, setAdditionalStudents] = useState<AdditionalStudent[]>([]);
  // Sections of the class ticked as homework for this session (block ids).
  const [homeworkBlockIds, setHomeworkBlockIds] = useState<string[]>([]);
  useEffect(() => {
    if (!data) return;
    const nextPresence: Record<string, boolean> = {};
    data.students.forEach((student) => {
      nextPresence[student.studentId] = student.isPresent ?? false;
    });
    setPresenceMap(nextPresence);
    setAdditionalStudents([]);
    setHomeworkBlockIds(data.homeworkBlockIds ?? []);
  }, [data]);

  const classBlocks = useMemo(
    () => [...(data?.class?.blocks ?? [])].sort((a, b) => a.order - b.order),
    [data],
  );
  const blocksWithExercises = useMemo(
    () => classBlocks.filter((block) => block.exerciseCount > 0).length,
    [classBlocks],
  );

  const toggleHomeworkBlock = (blockId: string, checked: boolean) => {
    setHomeworkBlockIds((prev) => {
      const without = prev.filter((id) => id !== blockId);
      return checked ? [...without, blockId] : without;
    });
  };

  const allStudents = useMemo(() => {
    if (!data) return [];
    const baseStudentIds = new Set(data.students.map((s) => s.studentId));
    const extra = additionalStudents.filter((s) => !baseStudentIds.has(s.studentId));
    return [
      ...data.students.map((s) => ({ ...s, addedByPicker: false })),
      ...extra.map((s) => ({ ...s, isPresent: presenceMap[s.studentId] ?? false, addedByPicker: true })),
    ];
  }, [data, additionalStudents, presenceMap]);

  const [attendanceSearch, setAttendanceSearch] = useState('');

  const filteredStudents = useMemo(() => {
    if (!attendanceSearch.trim()) return allStudents;
    const query = attendanceSearch.toLowerCase();
    return allStudents.filter(
      (s) =>
        s.fullName.toLowerCase().includes(query) ||
        (s.email && s.email.toLowerCase().includes(query)),
    );
  }, [allStudents, attendanceSearch]);

  const mentionOptions = useMemo<MentionOption[]>(
    () => allStudents.map((s) => ({ value: s.studentId, label: s.fullName })),
    [allStudents],
  );

  const studentCount = allStudents.length;
  const presentCount = useMemo(
    () => allStudents.filter((student) => presenceMap[student.studentId]).length,
    [allStudents, presenceMap],
  );

  const togglePresence = (studentId: string) => {
    setPresenceMap((prev) => ({
      ...prev,
      [studentId]: !prev[studentId],
    }));
  };

  const markAll = (value: boolean) => {
    const updated: Record<string, boolean> = {};
    allStudents.forEach((student) => {
      updated[student.studentId] = value;
    });
    setPresenceMap(updated);
  };

  const removeStudent = (studentId: string) => {
    setAdditionalStudents((prev) => prev.filter((s) => s.studentId !== studentId));
    setPresenceMap((prev) => {
      const next = { ...prev };
      delete next[studentId];
      return next;
    });
  };

  const handleSearch = useCallback(
    async (query: string): Promise<Option[]> => {
      if (query.trim().length < 3) return [];
      const users = await utils.users.searchActiveUsers.fetch({ search: query });
      const existingIds = new Set(allStudents.map((s) => s.studentId));
      return users
        .filter((u) => !existingIds.has(u.userId))
        .map((u) => ({
          value: u.userId,
          label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || 'User',
          email: u.email ?? undefined,
        }));
    },
    [utils, allStudents],
  );

  const pickerValue = useMemo<Option[]>(() => {
    return additionalStudents.map((s) => ({
      value: s.studentId,
      label: s.fullName,
    }));
  }, [additionalStudents]);

  const handlePickerChange = useCallback(
    (selected: Option[]) => {
      const selectedIds = new Set(selected.map((o) => o.value));
      const prevIds = new Set(additionalStudents.map((s) => s.studentId));

      // Mark newly added as present
      for (const option of selected) {
        if (!prevIds.has(option.value)) {
          setPresenceMap((prev) => ({ ...prev, [option.value]: true }));
        }
      }

      // Remove deselected from presenceMap
      for (const student of additionalStudents) {
        if (!selectedIds.has(student.studentId)) {
          setPresenceMap((prev) => {
            const next = { ...prev };
            delete next[student.studentId];
            return next;
          });
        }
      }

      setAdditionalStudents(
        selected.map((option) => {
          const existing = additionalStudents.find((s) => s.studentId === option.value);
          if (existing) return existing;
          return {
            studentId: option.value,
            fullName: option.label,
            email: (option as Option & { email?: string }).email ?? null,
          };
        }),
      );
    },
    [additionalStudents],
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!data || isPending) return;
    await saveScorecard({
      eventId,
      notes,
      presences: allStudents.map((student) => ({
        studentId: student.studentId,
        isPresent: presenceMap[student.studentId] ?? false,
      })),
      // Always sent (even empty) so unticking every section clears the marks.
      homeworkBlockIds,
    });
    clearNotesDraft();
  };

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Card className="border border-rose-50 bg-white">
          <CardHeader>
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-10 w-32" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">
            {t('teacher.scorecardPage.eyebrow')}
          </p>
          <h1 className="text-3xl font-semibold text-gray-900">
            {data.event.title ?? t('common.fallbacks.untitledClass')}
          </h1>
          <p className="text-sm text-gray-600">
            {data.event.startTime
              ? dateFormatter.format(new Date(data.event.startTime))
              : t('common.fallbacks.dateNotProvided')}
            {data.event.location ? ` • ${data.event.location}` : ''}
          </p>
        </div>
        <Badge variant="secondary" className="rounded-full bg-rose-50 text-[#cf2e2e]">
          {t('teacher.scorecardPage.presentCount', { present: presentCount, total: studentCount })}
        </Badge>
      </div>

      <Card className="border border-rose-50 bg-white shadow-sm">
        <CardHeader className="space-y-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-gray-900">
              {t('teacher.scorecardPage.summary.title')}
            </CardTitle>
            <Badge className={data.scorecardId ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {data.scorecardId
                ? t('teacher.scorecardPage.summary.statusUpdated')
                : t('teacher.scorecardPage.summary.statusPending')}
            </Badge>
          </div>
          <CardDescription className="text-sm text-gray-600">
            {t('teacher.scorecardPage.summary.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MentionTextarea
            value={notes}
            onChange={setNotes}
            mentions={mentionOptions}
            placeholder={t('teacher.scorecardPage.summary.placeholder')}
            className="min-h-[160px]"
          />
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" onClick={() => markAll(true)}>
              {t('teacher.scorecardPage.actions.markAllPresent')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => markAll(false)}>
              {t('teacher.scorecardPage.actions.clearMarks')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-rose-50 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl text-gray-900">
            {t('teacher.scorecardPage.attendance.title')}
          </CardTitle>
          <CardDescription className="text-sm text-gray-600">
            {t('teacher.scorecardPage.attendance.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {canAddStudents && (
            <MultipleSelector
              onSearch={handleSearch}
              value={pickerValue}
              onChange={handlePickerChange}
              placeholder={t('teacher.scorecardPage.attendance.searchPlaceholder')}
              delay={500}
              emptyIndicator={
                <p className="text-center text-sm text-gray-500">
                  {t('teacher.scorecardPage.attendance.noResults')}
                </p>
              }
            />
          )}
          {studentCount > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                className="rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm shadow-sm focus:border-[#cf2e2e] focus:outline-none"
                placeholder={t('teacher.filters.searchStudent')}
                value={attendanceSearch}
                onChange={(e) => setAttendanceSearch(e.target.value)}
              />
            </div>
          )}
          {studentCount === 0 && (
            <div className="rounded-lg border border-dashed border-rose-200 p-6 text-center text-sm text-gray-500">
              {t('teacher.scorecardPage.attendance.empty')}
            </div>
          )}
          {filteredStudents.map((student) => (
            <label
              key={student.studentId}
              className="flex items-center justify-between rounded-2xl border border-rose-50 bg-[#fff9fb] px-4 py-3 text-sm text-gray-800"
            >
              <div>
                <p className="font-semibold">{student.fullName}</p>
                <p className="text-xs text-gray-500">
                  {student.email ?? t('common.fallbacks.noEmail')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={presenceMap[student.studentId] ?? false}
                  onCheckedChange={() => togglePresence(student.studentId)}
                  className="data-[state=checked]:bg-[#cf2e2e]"
                />
                {canAddStudents && student.addedByPicker && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      removeStudent(student.studentId);
                    }}
                    className="rounded-full p-1 text-gray-400 hover:bg-rose-100 hover:text-[#cf2e2e]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </label>
          ))}
        </CardContent>
      </Card>

      <Card className="border border-rose-50 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl text-gray-900">
            {t('teacher.scorecardPage.homework.title')}
          </CardTitle>
          <CardDescription className="text-sm text-gray-600">
            {t('teacher.scorecardPage.homework.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!data.class ? (
            <div className="rounded-lg border border-dashed border-rose-200 p-6 text-center text-sm text-gray-500">
              {t('teacher.scorecardPage.homework.noClass')}
            </div>
          ) : (
            <>
              {classBlocks.map((block) => {
                const hasExercises = block.exerciseCount > 0;
                const checkboxId = `homework-block-${block.blockId}`;
                return (
                  <label
                    key={block.blockId}
                    htmlFor={checkboxId}
                    className={`flex items-center justify-between gap-3 rounded-2xl border border-rose-50 bg-[#fff9fb] px-4 py-3 text-sm text-gray-800 ${
                      hasExercises ? 'cursor-pointer' : 'opacity-60'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Checkbox
                        id={checkboxId}
                        checked={homeworkBlockIds.includes(block.blockId)}
                        disabled={!hasExercises}
                        onCheckedChange={(checked) => toggleHomeworkBlock(block.blockId, checked === true)}
                        className="border-rose-300 data-[state=checked]:border-[#cf2e2e] data-[state=checked]:bg-[#cf2e2e] data-[state=checked]:text-white"
                      />
                      <span className="truncate font-semibold">
                        {block.order + 1}. {block.name}
                      </span>
                    </div>
                    <span className="shrink-0 text-xs text-gray-500">
                      {hasExercises
                        ? t('teacher.scorecardPage.homework.exercises', { count: block.exerciseCount })
                        : t('teacher.scorecardPage.homework.noExercises')}
                    </span>
                  </label>
                );
              })}
              <p className="text-xs text-gray-500">
                {homeworkBlockIds.length === 0
                  ? t('teacher.scorecardPage.homework.allCount', { count: blocksWithExercises })
                  : t('teacher.scorecardPage.homework.markedCount', { count: homeworkBlockIds.length })}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button
          type="submit"
          disabled={isPending || studentCount === 0}
          className="rounded-full bg-[#cf2e2e] px-8 text-white hover:bg-[#b82424]"
        >
          {isPending
            ? t('common.status.saving')
            : t('teacher.scorecardPage.actions.save')}
        </Button>
      </div>
    </form>
  );
}
